import type { RootState } from "@/app/store";
import type { LayerState, SavedItem } from "@/types";
import esriConfig from '@arcgis/core/config';
import Collection from "@arcgis/core/core/Collection";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GroupLayer from "@arcgis/core/layers/GroupLayer";
import Layer from "@arcgis/core/layers/Layer";
import WebMap from "@arcgis/core/WebMap";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

// ArcGIS objects are stored as-is; store.ts exempts these paths from serializability checks.
export interface PQMapState {
    webMap: WebMap | null,
    pointLayer: FeatureLayer | null,
    polygonLayer: FeatureLayer | null,
    mapLayers: Collection<Layer>,
    notificationTable: FeatureLayer | null,
    thumbnailTable: FeatureLayer | null,
    displayLegendLayer: LayerState | null,
    loading: boolean,
    error: string | null,
    loadRequestId: string | null,
    zoomToSavedItem: SavedItem | null
}

export const loadPQMap = createAsyncThunk(
    'pqMap/loadPQMap',
    async (_: void, { signal }) => {
        esriConfig.apiKey = import.meta.env.VITE_ESRI_API_KEY as string;
        const webMap = new WebMap({
            portalItem: {
                id: import.meta.env.VITE_ESRI_WEBMAP_ITEM_ID as string,
                portal: {
                    url: import.meta.env.VITE_ESRI_PORTAL_URL as string,
                }
            }
        })
        let destroyed = false;
        const destroyMap = () => {
            if (destroyed) return;
            destroyed = true;
            webMap.destroy();
        };
        signal.addEventListener('abort', destroyMap, { once: true });
        try {
            await webMap.load();
            signal.throwIfAborted();
            const mapLayers = webMap.layers.filter((layer) => layer.title !== 'Parcels');
            webMap.allLayers.forEach((layer) => {
                (layer as FeatureLayer).popupEnabled = false;
            })
            const parcelLayers = (webMap.layers.find((layer) => layer.title === 'Parcels' && layer.type === 'group') as GroupLayer | undefined)?.layers;
            const polygonLayer = (parcelLayers?.find((layer) => layer.title === 'Statewide Parcel Polygons') ?? null) as FeatureLayer | null;
            const pointLayer = (parcelLayers?.find((layer) => layer.title === 'Statewide Parcel Points') ?? null) as FeatureLayer | null;
            const notificationTable = (webMap.tables.find((layer) => layer.title === 'Notifications') ?? null) as FeatureLayer | null;
            const thumbnailTable = (webMap.tables.find((layer) => layer.title === 'Thumbnails') ?? null) as FeatureLayer | null;
            if (!polygonLayer || !pointLayer) {
                throw new Error('The parcel map is missing a required layer. Please try again or contact support.');
            }
            polygonLayer.outFields = ['*'];
            return {webMap, mapLayers, polygonLayer, pointLayer, notificationTable, thumbnailTable};
        } catch (error) {
            destroyMap();
            throw error;
        } finally {
            signal.removeEventListener('abort', destroyMap);
        }
    }
)

export const initialState: PQMapState = {
    webMap: null,
    pointLayer: null,
    polygonLayer: null,
    mapLayers: new Collection<Layer>(),
    notificationTable: null,
    thumbnailTable: null,
    displayLegendLayer: null,
    loading: false,
    error: null,
    loadRequestId: null,
    zoomToSavedItem: null
}

export const pqMapSlice = createSlice({
    name: 'pqMap',
    initialState,
    reducers: {
        setDisplayLegendLayer: (state, action: PayloadAction<LayerState | null>) => {
            state.displayLegendLayer = action.payload;
        },
        setZoomToSavedItem: (state, action: PayloadAction<SavedItem | null>) => {
            state.zoomToSavedItem = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(loadPQMap.fulfilled, (state, action) => {
            if (state.loadRequestId !== action.meta.requestId) return;
            Object.assign(state, action.payload);
            state.loading = false;
            state.loadRequestId = null;
        });
        builder.addCase(loadPQMap.pending, (state, action) => {
            state.loading = true;
            state.error = null;
            state.loadRequestId = action.meta.requestId;
        });
        builder.addCase(loadPQMap.rejected, (state, action) => {
            if (state.loadRequestId !== action.meta.requestId) return;
            state.loading = false;
            state.loadRequestId = null;
            state.error = action.meta.aborted ? null : 'The map could not load. Check your connection and try again.';
        });
    }
})

export const { setDisplayLegendLayer, setZoomToSavedItem } = pqMapSlice.actions;

export const webMap = (state: RootState) => state.pqMap.webMap;
export const pointLayer = (state: RootState) => state.pqMap.pointLayer;
export const polygonLayer = (state: RootState) => state.pqMap.polygonLayer;
export const notificationTable = (state: RootState) => state.pqMap.notificationTable;
export const thumbnailTable = (state: RootState) => state.pqMap.thumbnailTable;
export const mapLayers = (state: RootState) => state.pqMap.mapLayers;
export const displayLegendLayer = (state: RootState) => state.pqMap.displayLegendLayer;
export const mapLoading = (state: RootState) => state.pqMap.loading;
export const mapError = (state: RootState) => state.pqMap.error;
export const zoomToSavedItem = (state: RootState) => state.pqMap.zoomToSavedItem;

export default pqMapSlice.reducer;
